import { DATA_SOURCE_TYPES, PARCELS_LAYER_ID } from '@/components/maplibre/consts';
import type { ColumnMetadata, DataSourceConfig, LayerStats } from '@/components/maplibre/types';
import { LayerTypeInfo } from '@/components/maplibre/types';
import { LAYER_STATUS } from '@/lib/db-constants';
import type { Layer, MapLayer } from '@/lib/db-row-types';
import {
  buildStatsDisplayNameMap,
  resolveColumnDisplayName,
} from '@/utils/layer-sources/column-display-names';
import { buildGeoJsonDataSources } from '@/utils/layer-sources/geojson-source-builder';
import { buildRasterDataSources } from '@/utils/layer-sources/raster-source-builder';
import { buildReadonlyTilesetDataSources } from '@/utils/layer-sources/readonly-tileset-source-builder';
import { buildTilesetDataSources } from '@/utils/layer-sources/tileset-source-builder';
import { fetchBoundaryInfo } from '@/utils/models/layers';
import { createClient } from '@/utils/supabase/client';

/**
 * Determines if a layer is a tileset or GeoJSON based on boundary_id or type
 * Note: Parcel layers (type='parcel') are now rendered as GeoJSON from stored geometry
 */
export function isLayerTileset(layer: Layer): boolean {
  // Parcel layers are rendered as GeoJSON from stored geometry, not as tilesets
  if (layer.type === 'parcel') {
    return false;
  }
  return Boolean(layer.boundary_id) || layer.type === 'tileset' || layer.type === 'core';
}

/**
 * Extracts join property
 */
export function extractJoinProperty(): string {
  //For all our existing cases, this should be 'id', but it might not be for new boundaries added to our system
  return 'id';
}

/**
 * Builds column metadata from a layer's `stats.fields`, used for layers that
 * don't have (or don't fully populate) `layer_columns` rows. The sanitized
 * `name` is the canonical identifier; `display_name` carries the original
 * label when the worker renamed the column.
 */
export function buildColumnMetadataFromStats(layer: Layer): ColumnMetadata[] {
  if (layer.stats && typeof layer.stats === 'object' && 'fields' in layer.stats) {
    const stats = layer.stats as unknown as LayerStats;
    if (Array.isArray(stats.fields)) {
      return stats.fields.map(field => ({
        column_id: field.id,
        column_name: field.name,
        display_name: field.display_name ?? null,
        is_read_only: true,
        // Carry the stats type so the filter panel can pick numeric/date
        // operators; without it every readonly-tileset column defaulted to the
        // value heuristic (and to text when no rows were loaded).
        data_type: field.type,
      }));
    }
  }
  return [];
}

/**
 * Fetches column metadata for all layers in a single batch query
 * For standard/tileset layers, fetches from layer_columns table (falling back
 * to stats.fields when no rows exist, e.g. tiled uploads)
 * For readonly tileset layers, extracts from layer.stats
 */
async function fetchAllColumnMetadata(layers: Layer[]): Promise<Map<string, ColumnMetadata[]>> {
  const columnMetadataMap = new Map<string, ColumnMetadata[]>();

  const tilesetLayers = layers.filter(
    layer =>
      layer.type === 'standard' ||
      (layer.type === 'tileset' && layer.boundary_id) ||
      layer.type === 'parcel' ||
      layer.type === 'integration_sync' ||
      layer.type === 'criterion_analysis'
  );
  const readonlyTilesetLayers = layers.filter(
    layer => (layer.type === 'tileset' && !layer.boundary_id) || layer.type === 'core'
  );

  if (tilesetLayers.length > 0) {
    const supabase = createClient();
    const layerIds = tilesetLayers.map(l => l.id);

    // Per-layer { sanitized -> original } map so layer_columns rows missing a
    // display_name (the common case — the worker only writes it to stats) still
    // surface original labels. See utils/layer-sources/column-display-names.
    const statsByLayer = new Map<string, Record<string, string>>();
    tilesetLayers.forEach(layer => {
      statsByLayer.set(layer.id, buildStatsDisplayNameMap(layer.stats));
    });

    const { data: columns, error } = await supabase
      .from('layer_columns')
      .select('id, column_name, display_name, data_type, is_read_only, layer_id')
      .in('layer_id', layerIds);

    if (!error && columns) {
      columns.forEach(col => {
        const statsDisplayNames = statsByLayer.get(col.layer_id) ?? {};
        const metadata: ColumnMetadata = {
          column_id: col.id,
          column_name: col.column_name,
          display_name: resolveColumnDisplayName(
            col.column_name,
            col.display_name,
            statsDisplayNames
          ),
          is_read_only: col.is_read_only,
          data_type: col.data_type || undefined,
        };

        const existing = columnMetadataMap.get(col.layer_id) || [];
        existing.push(metadata);
        columnMetadataMap.set(col.layer_id, existing);
      });
    }

    // Tiled uploads (e.g. `standard` layers) render properties straight from
    // tiles and never materialize layer_columns rows, so the query above finds
    // nothing for them. Fall back to stats.fields so their columns (and
    // original labels) are still available to style controls and popups.
    tilesetLayers.forEach(layer => {
      if (!columnMetadataMap.has(layer.id)) {
        const fromStats = buildColumnMetadataFromStats(layer);
        if (fromStats.length > 0) {
          columnMetadataMap.set(layer.id, fromStats);
        }
      }
    });
  }

  readonlyTilesetLayers.forEach(layer => {
    const fromStats = buildColumnMetadataFromStats(layer);
    if (fromStats.length > 0) {
      columnMetadataMap.set(layer.id, fromStats);
    }
  });

  return columnMetadataMap;
}

/**
 * Analyzes a layer and returns type information
 */
export async function analyzeLayerType(layer: Layer): Promise<LayerTypeInfo> {
  // Raster layers render from worker-served XYZ tiles, not vector geometry —
  // branch here before the tileset/GeoJSON heuristics would misclassify them.
  if (layer.type === 'raster') {
    return {
      layer,
      type: DATA_SOURCE_TYPES.RASTER,
    };
  }

  if (!isLayerTileset(layer)) {
    return {
      layer,
      type: DATA_SOURCE_TYPES.GEOJSON,
    };
  }

  if (layer.type === 'tileset' || layer.type === 'core') {
    return {
      layer,
      type: DATA_SOURCE_TYPES.READONLY_TILESET,
      joinProperty: extractJoinProperty(),
    };
  }

  const boundaryInfo = await fetchBoundaryInfo(layer.boundary_id!);

  if (!boundaryInfo) {
    return {
      layer,
      type: DATA_SOURCE_TYPES.GEOJSON,
    };
  }

  return {
    layer,
    type: DATA_SOURCE_TYPES.TILESET,
    boundaryInfo,
    joinProperty: extractJoinProperty(),
  };
}

/**
 * Main function to fetch data sources for layers using hybrid approach
 *
 * This function:
 * 1. Filters out layers with 'processing' or 'error' status
 * 2. Analyzes each layer to determine its type (GeoJSON vs Tileset)
 * 3. Fetches column metadata for all layers in a single batch query
 * 4. Groups layers by type
 * 5. Fetches data sources for each type in parallel
 * 6. Combines and returns all data sources
 */
export async function buildLayerDataSources(
  layers: Layer[],
  mapLayers: MapLayer[] = []
): Promise<DataSourceConfig[]> {
  if (layers.length === 0) {
    return [];
  }

  const readyLayers = layers.filter(layer => layer.status === LAYER_STATUS.OK);

  if (readyLayers.length === 0) {
    console.warn('No layers with "ok" status found, skipping data source building');
    return [];
  }

  try {
    const [layerTypeInfos, columnMetadataMap] = await Promise.all([
      Promise.all(readyLayers.map(layer => analyzeLayerType(layer))),
      fetchAllColumnMetadata(readyLayers),
    ]);

    const geojsonLayers = layerTypeInfos
      .filter(info => info.type === DATA_SOURCE_TYPES.GEOJSON)
      .map(info => info.layer);

    const tilesetLayerInfos = layerTypeInfos
      .filter(info => info.type === DATA_SOURCE_TYPES.TILESET)
      .sort((a, b) => {
        // Prioritize PARCELS_LAYER_ID if it exists
        // TODO: remove this once we implement a better layer ordering system
        if (a.layer.id === PARCELS_LAYER_ID) return -1;
        if (b.layer.id === PARCELS_LAYER_ID) return 1;
        return 0;
      });

    const readonlyTilesetLayerInfos = layerTypeInfos.filter(
      info => info.type === DATA_SOURCE_TYPES.READONLY_TILESET
    );

    const rasterLayerInfos = layerTypeInfos.filter(info => info.type === DATA_SOURCE_TYPES.RASTER);

    const [geojsonDataSources, tilesetDataSources, readonlyTilesetDataSources] = await Promise.all([
      buildGeoJsonDataSources(geojsonLayers, mapLayers, columnMetadataMap),
      buildTilesetDataSources(tilesetLayerInfos, mapLayers, columnMetadataMap),
      buildReadonlyTilesetDataSources(readonlyTilesetLayerInfos, mapLayers, columnMetadataMap),
    ]);

    const rasterDataSources = buildRasterDataSources(rasterLayerInfos, mapLayers);

    const allDataSources: DataSourceConfig[] = [
      ...geojsonDataSources,
      ...tilesetDataSources,
      ...readonlyTilesetDataSources,
      ...rasterDataSources,
    ];

    return allDataSources;
  } catch (error) {
    console.error('Error fetching layer data sources:', error);
    return [];
  }
}
